import { customAlphabet } from 'nanoid/non-secure';
import { clsx } from 'clsx';
import { twMerge } from 'tailwind-merge';
import i18n from '@i18n';

export const isAppleDevice = () => {
    const PLATFORM = typeof navigator === 'object' ? navigator.platform : '';
    return /Mac|iPod|iPhone|iPad/.test(PLATFORM);
};

export const modKey = isAppleDevice() ? '⌘' : 'Ctrl';

export const supportImg = [
    'image/jpeg',
    'image/png',
    'image/bmp',
    'image/gif',
    'image/webp',
];

export function cn(...inputs) {
    return twMerge(clsx(inputs));
}

// 7-character random string
export const nanoid = customAlphabet(
    '0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz',
    7
);

export const toDownloadFile = (url, name) => {
    // 对于Blob类型的数据，创建一个URL
    const dataUrl = url instanceof Blob ? URL.createObjectURL(url) : url;
    
    // 创建下载链接
    const link = document.createElement('a');
    link.href = dataUrl;
    link.download = name;
    document.body.appendChild(link);
    
    // 触发点击
    link.click();
    
    // 清理
    document.body.removeChild(link);
    if (url instanceof Blob) {
        URL.revokeObjectURL(dataUrl);
    }
};

export const computedSize = (w, h, maxWidth = 950, maxHeight = 450) => {
    let width = w;
    let height = h;

    // 检查图片是否超过最大宽度
    if (width > maxWidth) {
        height *= maxWidth / width;
        width = maxWidth;
    }

    // 检查图片是否超过最大高度
    if (height > maxHeight) {
        width *= maxHeight / height;
        height = maxHeight;
    }
    return { width: Math.round(width), height: Math.round(height) };
};

export const getImage = (src) => {
    const img = new Image();
    // cors
    if (!src.startsWith('data')) {
        img.crossOrigin = 'Anonymous';
    }
    return new Promise(function (resolve, reject) {
        img.onload = function () {
            resolve(img);
        };
        const errorHandler = function () {
            return reject(
                new Error('An error occurred attempting to load image')
            );
        };
        img.onerror = errorHandler;
        img.onabort = errorHandler;
        img.src = src;
    });
};

export const getMargin = (width, height, r = 0.15) => {
    const min = Math.min(width, height);
    return Math.round(min * r);
};

// type: ['top-left', 'top', 'top-right', 'left', 'center', 'right', 'bottom-left', 'bottom', 'bottom-right']
export const getPosition = (type, xw, xh) => {
    if (type === 'top-left')
        return {
            x: 0,
            y: 0,
        };
    if (type === 'top')
        return {
            x: xw / 2,
            y: 0,
        };
    if (type === 'top-right')
        return {
            x: xw,
            y: 0,
        };
    if (type === 'left')
        return {
            x: 0,
            y: xh / 2,
        };
    if (type === 'right')
        return {
            x: xw,
            y: xh / 2,
        };
    if (type === 'bottom-left')
        return {
            x: 0,
            y: xh,
        };
    if (type === 'bottom')
        return {
            x: xw / 2,
            y: xh,
        };
    if (type === 'bottom-right')
        return {
            x: xw,
            y: xh,
        };
    return { x: xw / 2, y: xh / 2 };
};

export const calculateRotatedRectDimensions = (width, height, angleDegrees) => {
    const angleRadians = angleDegrees * (Math.PI / 180);
    const newWidth =
        Math.abs(width * Math.cos(angleRadians)) +
        Math.abs(height * Math.sin(angleRadians));
    const newHeight =
        Math.abs(width * Math.sin(angleRadians)) +
        Math.abs(height * Math.cos(angleRadians));

    return {
        width: Math.round(newWidth),
        height: Math.round(newHeight),
    };
};

export function toBase64(blob) {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result);
        reader.onerror = reject;
        reader.readAsDataURL(blob);
    });
};

export const svgToDataURL = (svgStr) => {
    const encoded = encodeURIComponent(svgStr).replace(/'/g, '%27').replace(/"/g, '%22');

    const header = 'data:image/svg+xml,';
    const dataUrl = header + encoded;

    return dataUrl;
};

export const text2Svg = ({ text, color, angleDegrees, fontSize = 36, fontFamily = 'sans-serif' }) => {
    const div = document.createElement('div');
    div.style = `text-align:center;white-space:nowrap;line-height:100px;transform: rotate(${angleDegrees}deg);position: absolute;top:0;left:0;opacity: 0;`;
    const span = document.createElement('span');
    span.style.color = color;
    span.style.fontSize = `${fontSize}px`;
    span.style.fontFamily = fontFamily;
    span.innerText = text;
    div.append(span);
    document.body.append(div);
    const { width, height } = div.getBoundingClientRect();
    document.body.removeChild(div);
    const result = calculateRotatedRectDimensions(width, height, angleDegrees);
    const divHtml = `
        <div xmlns="http://www.w3.org/1999/xhtml" style="text-align:center;white-space:nowrap;line-height:${result.height}px;transform:rotate(${angleDegrees}deg);">
            <span style="color:${color};font-size:${fontSize}px;font-family:${fontFamily};">${text}</span>
        </div>
    `;
    const data = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${result.width} ${result.height}" width="${result.width}" height="${result.height}">
                <foreignObject width="100%" height="100%">
                    ${divHtml}
                </foreignObject>
            </svg>`;
    const url = svgToDataURL(data);
    return url;
};

export const numSvg = (num) => {
    const data = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 32 32" width="32" height="32">
                <foreignObject width="100%" height="100%">
                    <div xmlns="http://www.w3.org/1999/xhtml" style="text-align:center;white-space:nowrap;line-height:32px;">
                        <span style="color:#ffffff;font-size:18px;">${num}</span>
                    </div>
                </foreignObject>
            </svg>`;
    const url = svgToDataURL(data);
    return url;
};

/**
 * 处理背景配置，确保纹理背景能够正确应用
 * @param {Object} background - 背景配置对象
 * @returns {Object} - 处理后的背景配置
 */
export const processBackground = (background) => {
  if (!background) return background;
  
  // 开发调试日志
  if (background.type === 'pattern') {
    console.log(i18n.t('editor:utils.debug.processTextureBackground'), background);
  }
  
  // 如果是纹理背景（pattern类型）
  if (background.type === 'pattern') {
    // 根据Leafer UI文档，fill应该设置为一个对象数组
    return [
      {
        type: 'solid',
        color: background.backgroundColor || '#ffffff'
      },
      {
        type: 'image',
        url: background.url,
        width: background.width || 20,
        height: background.height || 20,
        repeat: true
      }
    ];
  }
  
  return background;
};

/**
 * 安全地从URL加载图片，处理CORS问题
 * @param {string} url - 图片URL
 * @returns {Promise<Blob>} - 返回图片Blob对象
 */
export const safeLoadImageFromUrl = (url) => {
  return new Promise((resolve, reject) => {
    // 如果是数据URL或同源URL，可以直接使用fetch
    if (url.startsWith('data:') || url.startsWith(window.location.origin)) {
      fetch(url)
        .then(response => {
          if (!response.ok) {
            throw new Error(`${i18n.t('editor:utils.error.requestFailed')} ${response.status}`);
          }
          return response.blob();
        })
        .then(resolve)
        .catch(reject);
      return;
    }
    
    // 处理跨域图片
    const img = new Image();
    img.crossOrigin = 'anonymous';
    
    img.onload = () => {
      try {
        // 创建canvas将图片转为data URL，避免CORS限制
        const canvas = document.createElement('canvas');
        canvas.width = img.width;
        canvas.height = img.height;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0);
        
        // 转换为blob
        canvas.toBlob(blob => {
          if (blob) {
            resolve(blob);
          } else {
            reject(new Error(i18n.t('editor:utils.error.cannotConvertImageFormat')));
          }
        }, 'image/png');
      } catch (error) {
        reject(error);
      }
    };
    
    img.onerror = () => {
      reject(new Error(i18n.t('editor:utils.error.imageLoadFailed')));
    };
    
    // 设置src开始加载
    img.src = url;
  });
};
